import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { postWalletWithdrawQuote } from "../api/endpoints/wallet/wallet";
import type { WithdrawQuote } from "../api/models";

/**
 * Simulação de saque on-chain (`POST /wallet/withdraw/quote`).
 *
 * Roda as mesmas validações do saque real (destino permitido, limites da rede)
 * sem persistir nem mover saldo, então as recusas aparecem antes da
 * confirmação. A taxa vem de lá, e não mais somada no front a partir de
 * `GET /wallet/withdraw/{coin}/networks`.
 */

interface ApiErrorDetail {
  message?: string;
  path?: string[];
}

interface ApiErrorEnvelope {
  message?: string;
  error?: {
    statusCode?: number;
    code?: string;
    details?: ApiErrorDetail[] | null;
  };
}

/** Erro normalizado da simulação. */
export class WithdrawQuoteError extends Error {
  readonly statusCode: number;
  readonly code: string | null;

  constructor(message: string, statusCode: number, code?: string | null) {
    super(message);
    this.name = "WithdrawQuoteError";
    this.statusCode = statusCode;
    this.code = code ?? null;
  }

  /** Coin ou rede inexistente no catálogo. */
  get isNotFound(): boolean {
    return this.statusCode === 404;
  }

  /** Valor fora dos limites da rede, payload inválido. */
  get isValidation(): boolean {
    return this.statusCode === 400;
  }

  /**
   * Destino recusado. A API responde 503 genérico de propósito — não revela a
   * restrição nem quais destinos estão liberados.
   */
  get isRejectedDestination(): boolean {
    return this.statusCode === 503;
  }

  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }
}

export interface UseWithdrawQuoteParams {
  coin: string;
  network: string;
  address: string;
  /** Valor que deve chegar ao destino, como digitado pelo usuário. */
  amount: string;
  tag?: string | null;
  /** Desliga a consulta enquanto o formulário não estiver pronto. */
  enabled?: boolean;
  options?: RequestInit;
  debounceMs?: number;
}

/** Espera o usuário parar de digitar antes de consultar a API. */
function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

export function useWithdrawQuote({
  coin,
  network,
  address,
  amount,
  tag = null,
  enabled = true,
  options,
  debounceMs = 500,
}: UseWithdrawQuoteParams) {
  const normalizedAmount = amount.replace(",", ".").trim();
  const normalizedAddress = address.trim();

  const debouncedAmount = useDebounced(normalizedAmount, debounceMs);
  const debouncedAddress = useDebounced(normalizedAddress, debounceMs);

  const amountNum = parseFloat(debouncedAmount);
  const isReady = enabled && !!coin && !!network &&
    debouncedAddress.length > 0 && Number.isFinite(amountNum) && amountNum > 0;

  const query = useQuery<WithdrawQuote, Error>({
    queryKey: [
      "withdraw-quote",
      coin,
      network,
      debouncedAddress,
      debouncedAmount,
      tag,
    ],
    enabled: isReady,
    retry: false,
    queryFn: async () => {
      const response = await postWalletWithdrawQuote({
        coin,
        network,
        address: debouncedAddress,
        amount: debouncedAmount,
        ...(tag ? { tag } : {}),
      }, options);

      if (response.status === 200 && "data" in response.data) {
        return response.data.data;
      }

      const body = (response.data ?? {}) as ApiErrorEnvelope;
      const detail = body.error?.details?.find((item) => item.message)?.message;

      throw new WithdrawQuoteError(
        detail ?? body.message ??
          `Não foi possível simular o saque (status ${response.status}).`,
        body.error?.statusCode ?? response.status,
        body.error?.code,
      );
    },
  });

  return {
    ...query,
    /**
     * true enquanto o usuário digita e a consulta ainda não saiu — evita
     * mostrar a taxa anterior como se fosse a atual.
     */
    isDebouncing: isReady &&
      (debouncedAmount !== normalizedAmount ||
        debouncedAddress !== normalizedAddress),
    isReady,
  };
}
